import {type Dispatch, useRef, useState} from 'react';

import {Avatar} from '../../../shared/components/avatar';
import {FaceCluster} from '../../../shared/components/faceCluster';
import {CrossIcon, GripIcon} from '../../../shared/components/icons';
import {useAutoGrow} from '../../../shared/hooks/useAutoGrow';
import {StickerCard} from '../../../shared/sticker/stickers';
import type {EditorAction, EditorOption} from '../editorState';
import {PersonPicker} from './personPicker';

// Starts as one line and grows with the text, so the card grows with it.
const ReasonField = ({
    label,
    value,
    disabled,
    onChange,
}: {
    label: string;
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}) => {
    const ref = useRef<HTMLTextAreaElement>(null);
    useAutoGrow(ref, value);
    return (
        <textarea
            ref={ref}
            className="bt-option__reason bt-autogrow"
            aria-label={label}
            placeholder="Why this one? (optional)"
            rows={1}
            maxLength={4000}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

const People = ({
    option,
    index,
    disabled,
    dispatch,
}: {
    option: EditorOption;
    index: number;
    disabled: boolean;
    dispatch: Dispatch<EditorAction>;
}) => (
    <div className="bt-chips">
        <span className="bt-chips__label">People</span>
        {option.people.map((person) => (
            <span key={person.humaansId} className="bt-chip">
                <Avatar src={person.photoUrl ?? undefined} name={person.name} size={20} />
                <span className="bt-chip__name">{person.name}</span>
                <button
                    type="button"
                    className="bt-chip__remove"
                    aria-label={`Remove ${person.name} from option ${index + 1}`}
                    disabled={disabled}
                    onClick={() =>
                        dispatch({
                            type: 'removePerson',
                            optionId: option.id,
                            humaansId: person.humaansId,
                        })
                    }
                >
                    <CrossIcon />
                </button>
            </span>
        ))}
        <PersonPicker
            label={`Add person to option ${index + 1}`}
            attached={new Set(option.people.map((p) => p.humaansId))}
            disabled={disabled}
            onPick={(person) => dispatch({type: 'addPerson', optionId: option.id, person})}
        />
    </div>
);

export const OptionList = ({
    options,
    disabled,
    dispatch,
}: {
    options: EditorOption[];
    disabled: boolean;
    dispatch: Dispatch<EditorAction>;
}) => {
    const [armed, setArmed] = useState<number | null>(null);
    const [from, setFrom] = useState<number | null>(null);

    return (
        <ul className="bt-options">
            {options.map((option, index) => (
                <li
                    key={option.id}
                    draggable={armed === index}
                    onDragStart={() => setFrom(index)}
                    onDragOver={(e) => from !== null && e.preventDefault()}
                    onDrop={() => {
                        if (from !== null && from !== index)
                            dispatch({type: 'moveOption', from, to: index});
                    }}
                    onDragEnd={() => {
                        setFrom(null);
                        setArmed(null);
                    }}
                    className={
                        from === index
                            ? 'bt-options__item bt-options__item--drag'
                            : 'bt-options__item'
                    }
                >
                    <StickerCard seed={option.id} className="bt-option">
                        <span
                            className="bt-option__grip"
                            aria-hidden="true"
                            onPointerDown={() => !disabled && setArmed(index)}
                            onPointerUp={() => setArmed(null)}
                        >
                            <GripIcon />
                        </span>
                        {option.people.length > 0 || option.title.trim() ? (
                            <FaceCluster
                                title={option.title || 'Option'}
                                photos={option.people.map((p) => p.photoUrl)}
                                names={option.people.map((p) => p.name)}
                                size={32}
                            />
                        ) : (
                            <span className="bt-option__blank" aria-hidden="true" />
                        )}
                        <div className="bt-option__body">
                            <input
                                className="bt-option__title"
                                aria-label={`Option ${index + 1} title`}
                                placeholder="Option title"
                                maxLength={120}
                                value={option.title}
                                disabled={disabled}
                                onChange={(e) =>
                                    dispatch({
                                        type: 'updateOption',
                                        id: option.id,
                                        patch: {title: e.target.value},
                                    })
                                }
                            />
                            <ReasonField
                                label={`Option ${index + 1} reason`}
                                value={option.description}
                                disabled={disabled}
                                onChange={(description) =>
                                    dispatch({
                                        type: 'updateOption',
                                        id: option.id,
                                        patch: {description},
                                    })
                                }
                            />
                            <People
                                option={option}
                                index={index}
                                disabled={disabled}
                                dispatch={dispatch}
                            />
                        </div>
                        <button
                            type="button"
                            className="bt-option__remove"
                            aria-label={`Remove ${option.title || `option ${index + 1}`}`}
                            disabled={disabled}
                            onClick={() => dispatch({type: 'removeOption', id: option.id})}
                        >
                            <CrossIcon />
                        </button>
                    </StickerCard>
                </li>
            ))}
        </ul>
    );
};
